export interface LocationPhoto {
  url: string;
  alt: string;
}

export interface Location {
  id: string;
  /** Number label shown on the map pin */
  number: number;
  name: string;
  nameJa: string;
  description: string;
  /** X position as % of the illustration image width (0-100) */
  x: number;
  /** Y position as % of the illustration image height (0-100) */
  y: number;
  /** Google Maps URL for directions */
  mapsUrl: string;
  photos: LocationPhoto[];
  tip?: string;
}

/**
 * Photo spots around Sensoji Temple.
 * Positions (x, y) are % of the illustration image from senso-ji.jp.
 *
 * The illustration has this layout (top to bottom):
 * - Top area: Main Hall (1), Yogodo (2), Awashimado (3)
 * - Middle: Pagoda (4), Denboin (5), Niten-mon (6)
 * - Lower middle: Hozomon (7), Kaminarimon (8)
 * - Bottom: Nakamise area (9), other structures (10)
 *
 * TODO: Replace placeholder photos with actual KRA kimono photos.
 * TODO: Fine-tune % coordinates after testing on mobile.
 */
export const locations: Location[] = [
  {
    id: "main-hall",
    number: 1,
    name: "Main Hall",
    nameJa: "本堂（観音堂）",
    description:
      "Sensoji's main hall — the spiritual heart of the temple. The grand entrance and incense smoke create a mystical, timeless setting for kimono photos.",
    x: 62,
    y: 18,
    mapsUrl: "https://maps.google.com/?q=Sensoji+Main+Hall,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1583766395091-2eb9994ed094?w=800&q=80", alt: "Main Hall front view" },
      { url: "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80", alt: "Incense ritual" },
      { url: "https://images.unsplash.com/photo-1578469645742-46cae010e5d6?w=800&q=80", alt: "Kimono at Main Hall" },
    ],
    tip: "Stand near the incense burner for atmospheric smoke effects in your photos",
  },
  {
    id: "yogodo",
    number: 2,
    name: "Yogodo Hall",
    nameJa: "影向堂",
    description:
      "A quieter hall where you can receive temple stamps (Goshuin). Beautiful traditional architecture with fewer crowds — perfect for peaceful shots.",
    x: 42,
    y: 12,
    mapsUrl: "https://maps.google.com/?q=35.71512,139.79606",
    photos: [
      { url: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80", alt: "Yogodo Hall" },
      { url: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80", alt: "Temple architecture" },
      { url: "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80", alt: "Quiet temple corner" },
    ],
    tip: "Get a Goshuin stamp as a beautiful souvenir — photo opportunity while waiting",
  },
  {
    id: "awashimado",
    number: 3,
    name: "Awashimado Hall",
    nameJa: "淡島堂",
    description:
      "A small hall tucked away in the temple grounds. Its intimate scale and traditional details make it ideal for close-up portrait shots.",
    x: 30,
    y: 22,
    mapsUrl: "https://maps.google.com/?q=35.71485,139.79510",
    photos: [
      { url: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80", alt: "Awashimado Hall" },
      { url: "https://images.unsplash.com/photo-1570459027562-4a916cc6113f?w=800&q=80", alt: "Temple detail" },
      { url: "https://images.unsplash.com/photo-1583766395091-2eb9994ed094?w=800&q=80", alt: "Traditional setting" },
    ],
    tip: "Less visited — you'll have more space for photos without crowds",
  },
  {
    id: "pagoda",
    number: 4,
    name: "Five-Storied Pagoda",
    nameJa: "五重塔",
    description:
      "Rising 53 meters into the sky. The pagoda's vertical lines pair beautifully with the flowing lines of kimono — perfect for dramatic portraits.",
    x: 38,
    y: 36,
    mapsUrl: "https://maps.google.com/?q=Five-story+Pagoda+Sensoji,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1478436127897-769e1b3f0f36?w=800&q=80", alt: "Five-storied Pagoda" },
      { url: "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80", alt: "Pagoda with sky" },
      { url: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80", alt: "Kimono portrait with pagoda" },
    ],
    tip: "Shoot from below looking up — dramatic vertical composition with kimono",
  },
  {
    id: "denboin",
    number: 5,
    name: "Denboin Garden",
    nameJa: "伝法院",
    description:
      "A hidden traditional garden with a pond reflecting the pagoda. National Scenic Beauty Site — quieter, intimate, romantic.",
    x: 30,
    y: 48,
    mapsUrl: "https://maps.google.com/?q=Denboin+Garden,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80", alt: "Denboin Garden" },
      { url: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80", alt: "Pond reflection" },
      { url: "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80", alt: "Garden path" },
    ],
    tip: "Less crowded, more personal — ideal for couple & solo kimono shots",
  },
  {
    id: "nitenmon",
    number: 6,
    name: "Niten-mon Gate",
    nameJa: "二天門",
    description:
      "An Important Cultural Property — this beautiful gate faces east and catches golden morning light. Smaller than Hozomon but equally photogenic.",
    x: 82,
    y: 42,
    mapsUrl: "https://maps.google.com/?q=35.71460,139.79750",
    photos: [
      { url: "https://images.unsplash.com/photo-1578469645742-46cae010e5d6?w=800&q=80", alt: "Niten-mon Gate" },
      { url: "https://images.unsplash.com/photo-1570459027562-4a916cc6113f?w=800&q=80", alt: "Gate detail" },
      { url: "https://images.unsplash.com/photo-1583766395091-2eb9994ed094?w=800&q=80", alt: "Kimono at gate" },
    ],
    tip: "Best in morning light — faces east, catches golden hour beautifully",
  },
  {
    id: "hozomon",
    number: 7,
    name: "Hozomon Gate",
    nameJa: "宝蔵門",
    description:
      "The grand inner gate with massive straw sandals (waraji) on the back. Looking through the gate creates stunning perspective shots.",
    x: 60,
    y: 52,
    mapsUrl: "https://maps.google.com/?q=Hozomon,Sensoji,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80", alt: "Hozomon Gate front" },
      { url: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80", alt: "Gate from behind" },
      { url: "https://images.unsplash.com/photo-1578469645742-46cae010e5d6?w=800&q=80", alt: "Walking through Hozomon" },
    ],
    tip: "Look back through the gate towards Nakamise for a beautiful framing shot",
  },
  {
    id: "kaminarimon",
    number: 8,
    name: "Kaminarimon Gate",
    nameJa: "雷門",
    description:
      "The iconic Thunder Gate — your first step into the world of kimono. The massive red lantern is THE most photographed spot in Asakusa.",
    x: 55,
    y: 82,
    mapsUrl: "https://maps.google.com/?q=Kaminarimon,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1583766395091-2eb9994ed094?w=800&q=80", alt: "Kaminarimon Gate" },
      { url: "https://images.unsplash.com/photo-1570459027562-4a916cc6113f?w=800&q=80", alt: "Red lantern close-up" },
      { url: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80", alt: "Traditional atmosphere" },
    ],
    tip: "Best time: early morning (8-9 AM) or evening for fewer crowds and soft light",
  },
  {
    id: "nakamise",
    number: 9,
    name: "Nakamise-dori",
    nameJa: "仲見世",
    description:
      "A vibrant 250-meter shopping street from Kaminarimon to Hozomon. The colorful storefronts and traditional goods create an authentic Japanese backdrop.",
    x: 55,
    y: 66,
    mapsUrl: "https://maps.google.com/?q=Nakamise-dori,Asakusa,Tokyo",
    photos: [
      { url: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80", alt: "Nakamise street" },
      { url: "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80", alt: "Traditional shops" },
      { url: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80", alt: "Walking through Nakamise" },
    ],
    tip: "Walk slowly, enjoy the atmosphere — great for candid walking shots",
  },
  {
    id: "benten",
    number: 10,
    name: "Benten-do Hall",
    nameJa: "弁天堂",
    description:
      "A small octagonal hall near a pond, dedicated to the goddess of music and art. A quiet, contemplative spot away from the main crowds.",
    x: 82,
    y: 52,
    mapsUrl: "https://maps.google.com/?q=35.71430,139.79770",
    photos: [
      { url: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80", alt: "Benten-do Hall" },
      { url: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80", alt: "Pond near Benten-do" },
      { url: "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80", alt: "Quiet corner" },
    ],
    tip: "Hidden gem — almost no tourists, perfect for artistic shots",
  },
];
